import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Leaf, Star, ChevronLeft, ChevronRight, Sparkles, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getProductRecommendations } from '@/lib/api';

export default function ProductDetailModal({ product, isOpen, onClose, onInquiry }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Recommendations state
  const [bundleData, setBundleData] = useState(null);
  const [loadingBundle, setLoadingBundle] = useState(false);

  const allImages = product?.images?.length > 0
    ? product.images
    : product?.image_url ? [product.image_url] : [];

  const startAutoPlay = useCallback(() => {
    clearInterval(timerRef.current);
    if (allImages.length <= 1) return;
    timerRef.current = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % allImages.length);
    }, 4000);
  }, [allImages.length]);

  useEffect(() => {
    if (!isOpen || paused) { clearInterval(timerRef.current); return; }
    startAutoPlay();
    return () => clearInterval(timerRef.current);
  }, [isOpen, paused, startAutoPlay]);

  // Fetch hospitality bundle recommendations
  useEffect(() => {
    if (isOpen && product?.id) {
      setLoadingBundle(true);
      getProductRecommendations(product.id)
        .then((res) => setBundleData(res.data))
        .catch(() => setBundleData(null))
        .finally(() => setLoadingBundle(false));
    } else {
      setBundleData(null);
    }
  }, [isOpen, product?.id]);

  const goTo = (idx) => {
    setActiveIdx(idx);
    setPaused(true);
    setTimeout(() => setPaused(false), 6000);
  };

  const handlePrev = () => goTo(activeIdx > 0 ? activeIdx - 1 : allImages.length - 1);
  const handleNext = () => goTo(activeIdx < allImages.length - 1 ? activeIdx + 1 : 0);

  const handleBundleInquiry = () => {
    if (!bundleData?.recommendations?.length) {
      onInquiry(product);
      onClose();
      return;
    }
    const complements = bundleData.recommendations.map(r => r.name).join(' + ');
    onInquiry({
      ...product,
      name: `${product.name} [Hospitality Bundle with ${complements}]`,
    });
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { onClose(); setActiveIdx(0); setPaused(false); } }}>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden rounded-2xl max-h-[90vh] flex flex-col md:flex-row" data-testid="product-detail-modal">
        <button onClick={onClose} className="absolute top-3 right-3 z-20 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/60 transition-colors" data-testid="product-detail-close" aria-label="Close product details">
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col md:flex-row w-full overflow-y-auto">
          {/* Image Gallery */}
          <div className="md:w-1/2 bg-gray-50 flex flex-col">
            <div className="group relative aspect-square overflow-hidden" data-testid="product-detail-main-image">
              {allImages.length > 0 ? (
                allImages.map((url, idx) => (
                  <img key={idx} src={url} alt={`${product.name} ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-contain p-4 bg-white transition-all duration-500 ease-in-out transform group-hover:scale-105 ${idx === activeIdx ? 'opacity-100' : 'opacity-0'}`} />
                ))
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-sm">No image</div>
              )}

              {allImages.length > 1 && (
                <>
                  <button onClick={handlePrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                    data-testid="product-detail-prev"
                    aria-label="Previous product image">
                    <ChevronLeft className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                    data-testid="product-detail-next"
                    aria-label="Next product image">
                    <ChevronRight className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {allImages.map((_, idx) => (
                      <button key={idx} onClick={() => goTo(idx)}
                        className={`rounded-full transition-all duration-300 ${idx === activeIdx ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'}`}
                        data-testid={`product-detail-dot-${idx}`}
                        aria-label={`Go to image ${idx + 1}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-1.5 p-3 overflow-x-auto bg-gray-50 border-t border-gray-100" data-testid="product-detail-thumbnails">
                {allImages.map((url, idx) => (
                  <button key={idx} onClick={() => goTo(idx)}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === activeIdx ? 'border-primary ring-1 ring-primary/30' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                    data-testid={`product-detail-thumb-${idx}`}
                    aria-label={`Select product image ${idx + 1}`}>
                    <img src={url} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="md:w-1/2 p-6 flex flex-col justify-between">
            <div>
              <div className="flex gap-2 mb-3">
                {product.match_badge && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-xs">
                    <Sparkles className="w-3 h-3 animate-pulse" /> {product.match_badge}
                  </span>
                )}
                {product.is_eco_friendly && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-[10px] font-semibold uppercase tracking-wider rounded-full">
                    <Leaf className="w-3 h-3" /> Eco-Friendly
                  </span>
                )}
                {product.is_featured && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#D4AF37] text-white text-[10px] font-semibold uppercase tracking-wider rounded-full">
                    <Star className="w-3 h-3" /> Best Seller
                  </span>
                )}
              </div>

              {product.category_name && (
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-2">
                  {product.category_name}
                </p>
              )}

              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }} data-testid="product-detail-name">
                {product.name}
              </h2>

              <p className="text-sm text-gray-600 leading-relaxed mb-4" data-testid="product-detail-description">
                {product.description}
              </p>

              {product.features?.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Features</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.features.map((f, i) => (
                      <span key={i} className="text-xs px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md font-medium">{f}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Frequently Paired in Hospitality (Bundle Recommendations) */}
              {bundleData?.enabled !== false && bundleData?.recommendations?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200/80 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-bold text-gray-900 tracking-wide">
                        Frequently Paired In Hospitality
                      </span>
                    </div>
                    <span className="text-[10px] text-amber-800 bg-amber-100 font-semibold px-2 py-0.5 rounded">
                      {bundleData.bundle_badge || 'Room Suite'}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mb-3 leading-snug">
                    {bundleData.bundle_tagline}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {bundleData.recommendations.slice(0, 2).map((rec) => (
                      <div key={rec.id} className="flex items-center gap-2 p-2 rounded-lg bg-stone-50 border border-stone-200/60">
                        <img
                          src={rec.image_url || '/assets/images/placeholder.png'}
                          alt={rec.name}
                          className="w-10 h-10 object-contain rounded bg-white p-1 border border-stone-200 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-gray-900 truncate">{rec.name}</p>
                          <p className="text-[10px] text-emerald-700 font-medium">
                            {rec.is_eco_friendly ? '🌿 Eco-Friendly' : '★ Hospitality Standard'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
              {bundleData?.enabled !== false && bundleData?.recommendations?.length > 0 && (
                <Button
                  onClick={handleBundleInquiry}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-lg font-semibold h-10 text-xs shadow-sm"
                  data-testid="product-detail-bundle-inquiry-btn">
                  <Layers className="w-3.5 h-3.5 mr-1.5" /> Inquire for Complete Hospitality Bundle
                </Button>
              )}
              <Button
                onClick={() => { onInquiry(product); onClose(); }}
                variant={bundleData?.enabled !== false && bundleData?.recommendations?.length > 0 ? "outline" : "default"}
                className={`w-full rounded-lg font-semibold h-10 text-xs ${
                  bundleData?.enabled !== false && bundleData?.recommendations?.length > 0
                    ? "border-[#14532D] text-[#14532D] hover:bg-[#14532D] hover:text-white"
                    : "bg-[#14532D] hover:bg-[#14532D]/90 text-white"
                }`}
                data-testid="product-detail-inquiry-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send Single Item Inquiry
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

